import { PRESET_PERMISSIONS, type PermissionScope, type PresetRole } from '@projoint/shared';

import { visibleSettingsTabs } from '@/pages/settings/settings-tabs';
import messages from '@/shared/i18n/en/team-tabs';
import { tr } from '@/shared/i18n/translate';
import { term } from '@/shared/lib/glossary';

/**
 * Team page tabs (docs/release-1/17-V2-BUILD-PLAN.md §6 W5-A; Appendix A §3.2):
 *
 *   Overview · Projects · Chat · Docs · Members · Time (Lead+) · Settings
 *
 * One table for the desktop tab bar and the phone page, so a role sees the
 * same set on both. Per-team role, never a global switch (§2.5):
 *  - Contractor (OUTSTAFF): "Your tasks" (instead of Overview), Projects,
 *    Chat, Docs — nothing team-wide;
 *  - Guest (VIEWER): read-only Overview, Projects, Chat, Docs, Members;
 *  - Time needs `time.view_team` (Lead+); Settings shows for every role that has
 *    a settings tab (a Member gets Members · Trust Center · Danger zone › Leave);
 *  - a Personal space has no Overview, Chat or Members: Projects · Docs · Settings.
 *
 * Overview / Projects / Members are panels of `/teams/:slug` (`?tab=`); Docs
 * and Time are their own routes; Chat is the one Chat filtered to the team
 * (`/my/messages?team=`); Settings is Team settings (`/settings?team=`).
 */
export const TEAM_TAB_IDS = [
  'overview',
  'projects',
  'chat',
  'docs',
  'members',
  'time',
  'settings',
] as const;
export type TeamTabId = (typeof TEAM_TAB_IDS)[number];

/** The tabs that are panels of the team index route (`?tab=`). */
export const TEAM_PANEL_TABS = ['overview', 'projects', 'members'] as const;
export type TeamPanelTab = (typeof TEAM_PANEL_TABS)[number];

export function isTeamPanelTab(v: unknown): v is TeamPanelTab {
  return typeof v === 'string' && (TEAM_PANEL_TABS as readonly string[]).includes(v);
}

export interface TeamTabsTeam {
  kind: 'personal' | 'regular';
  /** My preset (a custom role reports the preset it derives from). */
  myRolePreset: PresetRole | null;
}

function can(role: PresetRole | null, scope: PermissionScope): boolean {
  return !!role && (PRESET_PERMISSIONS[role]?.has(scope) ?? false);
}

/** The tabs this viewer gets, in bar order. */
export function visibleTeamTabs(team: TeamTabsTeam | null | undefined): TeamTabId[] {
  if (!team) return [];
  const role = team.myRolePreset;
  if (team.kind === 'personal') return ['projects', 'docs', 'settings'];
  const contractor = role === 'OUTSTAFF';
  return TEAM_TAB_IDS.filter((id) => {
    switch (id) {
      case 'overview':
      case 'projects':
      case 'docs':
        return true;
      case 'chat':
        return can(role, 'channel.view');
      case 'members':
        return !contractor && can(role, 'member.view');
      case 'time':
        return can(role, 'time.view_team');
      case 'settings':
        // Every role has some team settings (Members, Trust Center, Danger zone
        // › Leave team — as in 68f6cbe); the settings rail hides the admin tabs.
        return visibleSettingsTabs({ kind: 'regular', myRolePreset: role }).length > 0;
    }
  });
}

/** The panel `/teams/:slug` opens on: Overview, or Projects for a Personal space. */
export function defaultTeamPanel(team: TeamTabsTeam | null | undefined): TeamPanelTab {
  return team?.kind === 'personal' ? 'projects' : 'overview';
}

/** `?tab=` → the panel to show, falling back when this viewer can't see it. */
export function resolveTeamPanel(
  requested: unknown,
  team: TeamTabsTeam | null | undefined,
): TeamPanelTab {
  const visible = visibleTeamTabs(team);
  if (isTeamPanelTab(requested) && visible.includes(requested)) return requested;
  return defaultTeamPanel(team);
}

/** The tab label; a Contractor's Overview reads "Your tasks". */
export function teamTabLabel(id: TeamTabId, team?: TeamTabsTeam | null): string {
  switch (id) {
    case 'overview':
      return team?.myRolePreset === 'OUTSTAFF' ? tr(messages, 'tabYourTasks') : tr(messages, 'tabOverview');
    case 'projects':
      return tr(messages, 'tabProjects');
    case 'chat':
      return term('chat');
    case 'docs':
      return term('docs');
    case 'members':
      return tr(messages, 'tabMembers');
    case 'time':
      return term('time');
    case 'settings':
      return tr(messages, 'tabSettings');
  }
}

/** Where a tab goes — a typed router target. */
export type TeamTabTarget =
  | { to: '/teams/$teamSlug'; params: { teamSlug: string }; search: { tab?: TeamPanelTab } }
  | { to: '/teams/$teamSlug/docs' | '/teams/$teamSlug/time'; params: { teamSlug: string } }
  | { to: '/my/messages'; search: { team: string } }
  | { to: '/settings'; search: { team: string } };

export function teamTabTarget(id: TeamTabId, teamSlug: string): TeamTabTarget {
  const params = { teamSlug };
  switch (id) {
    case 'overview':
      return { to: '/teams/$teamSlug', params, search: {} };
    case 'projects':
    case 'members':
      return { to: '/teams/$teamSlug', params, search: { tab: id } };
    case 'docs':
      return { to: '/teams/$teamSlug/docs', params };
    case 'time':
      return { to: '/teams/$teamSlug/time', params };
    case 'chat':
      return { to: '/my/messages', search: { team: teamSlug } };
    case 'settings':
      return { to: '/settings', search: { team: teamSlug } };
  }
}

/** Route ids (TanStack) of the team-level pages that sit under the tab bar. */
const ROUTE_TAB: Readonly<Record<string, TeamTabId>> = {
  '/teams/$teamSlug/docs': 'docs',
  '/teams/$teamSlug/time': 'time',
  '/teams/$teamSlug/billing': 'settings',
};

/**
 * Which tab is active for the current leaf route, or null when the route is
 * not a team-level page (a project, a task, the chat redirect …).
 */
export function activeTeamTab(
  routeId: string | undefined,
  search: unknown,
  team: TeamTabsTeam | null | undefined,
): TeamTabId | null {
  if (!routeId) return null;
  if (routeId === '/teams/$teamSlug/') {
    return resolveTeamPanel((search as { tab?: unknown } | undefined)?.tab, team);
  }
  return ROUTE_TAB[routeId] ?? null;
}
